import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCacheCgdo } from 'app/shared/queries/useCacheCgdo';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';

// Components
import {
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';
import { IconCreditCard } from '@u21/tabler-icons';

// Selectors
import {
  selectAgentOrgID,
  selectHasReadInstrumentsPermission,
} from 'app/modules/session/selectors';

// Constants
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

// Helpers
import { formatInstrumentOptions } from 'app/modules/search/helpers';
import { InstrumentSearchRequest } from 'app/modules/search/models';
import { useQueryClient } from '@tanstack/react-query';
import {
  searchInstrumentsV1,
  useSearchInstrumentsV1,
} from 'app/modules/search/queries/useSearchInstruments';
import { SEARCH_QUERY_KEYS } from 'app/modules/search/queries/keys';
import { InstrumentSearchOptions, MatchType } from 'app/modules/search/enum';
import {
  searchInstruments,
  useInstrumentSearch,
} from 'app/modules/instruments/queries/useInstrumentSearch';
import { selectSearchV2Enabled } from 'app/shared/featureFlags/selectors';
import { INSTRUMENT_QUERY_KEYS } from 'app/modules/instruments/queries/keys';
import { InstrumentSearchPayload } from 'app/modules/search/payload';

export type U21InstrumentSearchProps<TClearable extends boolean> =
  DistributiveOmit<
    U21SelectSearchProps<string, TClearable>,
    'options' | 'onSearch'
  >;

export const U21InstrumentSearch = <TClearable extends boolean>(
  props: U21InstrumentSearchProps<TClearable>,
) => {
  const { multi, value } = props;

  const queryClient = useQueryClient();

  const isSearchV2Enabled = useSelector(selectSearchV2Enabled);
  const hasReadInstrumentsPermissions = useSelector(
    selectHasReadInstrumentsPermission,
  );
  const [search, setSearch] = useState<string | undefined>('');
  const [newPayload, setNewPayload] = useState<InstrumentSearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');

  const isV2SearchUsed = useMemo(
    () => Boolean(isSearchV2Enabled),
    [isSearchV2Enabled],
  );

  const {
    instruments: instrumentV1,
    query: { isFetching: isFetchingV1 },
  } = useSearchInstrumentsV1(
    {
      limit: INFINITE_SCROLL_LIMIT,
      offset: OFFSET_DEFAULT,
      phrase: search || '',
      type: InstrumentSearchOptions.EXTERNAL_ID,
    },
    !isV2SearchUsed,
  );

  const {
    instruments: instrumentV2,
    query: { isFetching: isFetchingV2 },
    refreshInstruments,
  } = useInstrumentSearch(newPayload, isV2SearchUsed);

  const instruments = useMemo(
    () => (isV2SearchUsed ? instrumentV2 : instrumentV1),
    [instrumentV1, instrumentV2, isV2SearchUsed],
  );

  const isFetching = useMemo(
    () => (isV2SearchUsed ? isFetchingV2 : isFetchingV1),
    [isFetchingV1, isFetchingV2, isV2SearchUsed],
  );

  const options = useMemo(
    () => formatInstrumentOptions(instruments),
    [instruments],
  );

  const orgId = useSelector(selectAgentOrgID);
  const { mutate: cacheCgdo } = useCacheCgdo();

  const cachePrefetch = useCallback(
    (newValues: string[]) => {
      if (!isV2SearchUsed) return;
      const prevSet = new Set(Array.isArray(value) ? value : []);
      newValues.forEach((i) => {
        if (prevSet.has(i)) return;
        cacheCgdo({
          object_type: 'instrument',
          hashed_id: computeDlHashIdForCgdo(orgId, String(i)),
        });
      });
    },
    [cacheCgdo, isV2SearchUsed, orgId, value],
  );

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!hasReadInstrumentsPermissions) {
      return;
    }

    let missingValues: string[] = [];

    if (Array.isArray(value)) {
      missingValues = value.filter((i) => !options.find((j) => j.value === i));
    } else if (value !== undefined) {
      const valueExists = options.find((i) => i.value === value);
      if (!valueExists) {
        missingValues = [value];
      }
    }

    if (missingValues.length && !calledRef.current) {
      calledRef.current = true;

      if (isV2SearchUsed) {
        const payload: InstrumentSearchPayload = {
          offset: OFFSET_DEFAULT,
          limit: missingValues.length,
          search_conditions: [
            {
              phrases: missingValues.map(String),
              field: InstrumentSearchOptions.EXTERNAL_ID,
              match_type: MatchType.EXACT,
            },
          ],
        };

        queryClient.prefetchQuery({
          queryKey: INSTRUMENT_QUERY_KEYS.getInstrumentSearch(payload),
          queryFn: () => searchInstruments(payload),
        });

        setNewPayload(payload);
      } else {
        const searchPayload: InstrumentSearchRequest = {
          phrase: '',
          limit: missingValues.length,
          offset: OFFSET_DEFAULT,
          type: 'external_id',
          external_ids: missingValues,
        };

        queryClient.prefetchQuery({
          queryKey: SEARCH_QUERY_KEYS.searchInstrumentsV1(searchPayload),
          queryFn: () => searchInstrumentsV1(searchPayload),
        });
      }
    }
  }, [
    hasReadInstrumentsPermissions,
    isV2SearchUsed,
    options,
    queryClient,
    value,
  ]);

  const onSearch = useCallback(
    (query?: string) => {
      setSearchValue(query || '');

      if (query) {
        if (isV2SearchUsed) {
          setNewPayload({
            offset: OFFSET_DEFAULT,
            limit: 10,
            search_conditions: [
              {
                phrases: [query],
                field: InstrumentSearchOptions.EXTERNAL_ID,
                match_type: MatchType.EXACT,
              },
            ],
          });
        } else {
          setSearch(query);
        }
      }
    },

    [isV2SearchUsed],
  );

  if (!hasReadInstrumentsPermissions) {
    return null;
  }

  const sharedProps: DistributivePick<
    U21SelectSearchProps<string, TClearable>,
    | 'loading'
    | 'options'
    | 'onExactSearch'
    | 'onSearch'
    | 'startIcon'
    | 'noOptionsText'
  > = {
    loading: isFetching,
    options,
    onExactSearch: isV2SearchUsed
      ? async (searchTerms: string[]) => {
          const payload: InstrumentSearchPayload = {
            offset: OFFSET_DEFAULT,
            limit: searchTerms.length,
            search_conditions: [
              {
                phrases: searchTerms.map(String),
                field: InstrumentSearchOptions.EXTERNAL_ID,
                match_type: MatchType.EXACT,
              },
            ],
          };

          await queryClient.prefetchQuery({
            gcTime: Infinity,
            queryKey: INSTRUMENT_QUERY_KEYS.getInstrumentSearch(payload),
            queryFn: () => searchInstruments(payload),
          });
          refreshInstruments();
        }
      : undefined,
    onSearch,
    startIcon: <IconCreditCard />,
    noOptionsText: searchValue
      ? 'No results found'
      : 'Type to search or paste a list of Instrument IDs',
  };

  if (multi) {
    const { onChange, ...rest } = props;
    return (
      <U21SelectSearch<string, TClearable>
        {...sharedProps}
        {...rest}
        multi
        onChange={(newValues, e) => {
          cachePrefetch(newValues);
          onChange?.(newValues, e);
        }}
      />
    );
  }
  const { onChange, ...rest } = props;
  return (
    <U21SelectSearch<string, TClearable>
      {...sharedProps}
      {...rest}
      onChange={(newValue, e) => {
        onChange?.(newValue, e);
      }}
    />
  );
};
